import { ClientError } from 'graphql-request';

import { AuthError, authClient, toAuthError } from '@/lib/auth';
import {
  ProfilePictureCompleteUploadDocument,
  ProfilePictureCreateUploadDocument,
} from '@/lib/generated/graphql';
import { graphEndpoint, request } from '@/lib/graph';

/**
 * The staff member's own picture (`a-staff-member-has-a-face`): the graph's three-step
 * upload, then the account write. No React and no state here — the dialog settles the
 * caches (D9).
 *
 * MathGaps/schools does all of this already for a teacher
 * (`apps/web/src/features/settings/ProfilePicture.tsx` and `src/lib/upload.ts`); A5 forbids
 * importing it, so the pattern is written here. `updateAccountPicture` belongs in
 * `src/lib/account.ts` the day `settings-is-a-dialog` lands — a move, not a rewrite (D4).
 */

/** The four kinds the graph sniffs for a `PICTURE`; the picker offers these and the rule is checked again (D8). */
export const PICTURE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

/** `accept` for the file input, which is a hint rather than a gate. */
export const PICTURE_ACCEPT = PICTURE_TYPES.join(',');

/** The graph's own cap for a picture (`MAX_USER_UPLOAD_IMAGE_BYTES`), not a tighter one of ours (D8). */
export const MAX_PICTURE_BYTES = 20 * 1024 * 1024;

/** Which prefix a picture is filed under. The graph refuses a `PICTURE` that names none (D2a). */
const PICTURE_CATEGORY = 'LESSON_COVER';

/** Why a chosen file was not sent, or `null` where it may be. Nothing is requested for a refusal. */
export function refusalOf(file: File): string | null {
  if (!(PICTURE_TYPES as readonly string[]).includes(file.type)) {
    return 'That is not a picture. Choose a PNG, JPEG, WebP or GIF.';
  }
  if (file.size === 0) return 'That file is empty. Choose another picture.';
  if (file.size > MAX_PICTURE_BYTES) {
    return `That picture is ${megabytes(file.size)} MB. The largest allowed is ${megabytes(MAX_PICTURE_BYTES)} MB.`;
  }
  return null;
}

function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '');
}

/** The `PUT` itself failed: the bucket's status, or `0` where the request was never answered (D11). */
export class UploadError extends Error {
  readonly status: number;

  constructor(message: string, status: number, options?: ErrorOptions) {
    super(message, options);
    this.name = 'UploadError';
    this.status = status;
  }
}

/** The graph refused a step in its own words; anything else keeps the step's sentence (D11). */
function graphReason(error: unknown): string | null {
  if (!(error instanceof ClientError)) return null;
  const first = error.response.errors?.[0];
  return first?.message ?? null;
}

/**
 * Send a picture and get its address back (D2, D3, D9).
 *
 * The `PUT` goes to the address `tsCreateUpload` minted for this one object, carrying the
 * headers it named — the signature fixes the object's name, its content type and its exact
 * byte count, so nothing else will do. `replacesUrl` retires the picture this one takes the
 * place of, and only once this one is known good.
 */
export async function uploadPicture(
  file: File,
  replaces: string | null,
  /** Called once the ticket is in hand and the bytes are about to move, for the step line (D10). */
  onSending?: () => void,
): Promise<string> {
  const { tsCreateUpload: ticket } = await request(
    ProfilePictureCreateUploadDocument,
    {
      input: {
        purpose: 'PICTURE',
        category: PICTURE_CATEGORY,
        fileName: file.name,
        /* The browser's claim. The signature fixes it and the completion never trusts it. */
        contentType: file.type,
        sizeBytes: file.size,
      },
    },
  );

  /*
   * A development server mints a PATH, which is its own route under the endpoint that answered —
   * on the same cluster, so it is joined onto the cluster's endpoint (a `new URL` would drop the
   * `/au` or `/us`).
   */
  const url = ticket.url.startsWith('http')
    ? ticket.url
    : `${graphEndpoint().replace(/\/+$/, '')}/${ticket.url.replace(/^\/+/, '')}`;

  onSending?.();
  let sent: Response;
  try {
    sent = await fetch(url, {
      method: 'PUT',
      headers: Object.fromEntries(
        ticket.headers.map(({ name, value }) => [name, value]),
      ),
      body: file,
    });
  } catch (error) {
    /* A bucket whose CORS refuses this origin and a dead network are the same bare TypeError here (D15). */
    throw new UploadError(
      'Could not send that picture: storage did not answer. Try again.',
      0,
      { cause: error },
    );
  }
  if (!sent.ok) {
    throw new UploadError(
      `Could not send that picture (${String(sent.status)}). Try again.`,
      sent.status,
    );
  }

  const { tsCompleteUpload } = await request(
    ProfilePictureCompleteUploadDocument,
    { uploadId: ticket.uploadId, replacesUrl: replaces },
  );
  if (!tsCompleteUpload.url) {
    throw new Error('The picture was stored without an address.');
  }
  return tsCompleteUpload.url;
}

/**
 * Put the picture on the account, or `null` to go back to the initial (D4, D7).
 *
 * Better Auth's own `image`, which the Worker takes as an `https` link or nothing
 * (`services/auth` `pictureLink`), so the session carries it to every disc that draws this
 * staff member.
 */
export async function updateAccountPicture(url: string | null): Promise<void> {
  try {
    const { error } = await authClient.updateUser({ image: url });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/** What a refused step says: the service's own sentence wherever it sent one (D11). */
export function reasonFor(error: unknown): string {
  if (error instanceof UploadError || error instanceof AuthError)
    return error.message;
  const refused = graphReason(error);
  if (refused !== null) return refused;
  return error instanceof Error && error.message !== ''
    ? error.message
    : 'Could not save that picture. Try again.';
}
